import { paths } from '~/singlePageApp/paths';
import { isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { parse } from 'date-fns';
import type { OnboardingChecklist, OnboardingChecklistItemData } from '../types';
import type { DefaultChecklistInfoQuery } from '../../gql/__gql/DefaultChecklistInfo.gql.gen';
import { TARGET_SELECTORS } from '../config';

export const LINK_TYPES = {
  analytics: 'first_analytics',
  customize: 'first_customization',
  edit: 'first_edit',
  media: TARGET_SELECTORS.createFromAnywhere,
  comment: 'first_comment',
  share: 'first_share',
};

const FIRST_MEDIA: OnboardingChecklistItemData = {
  name: 'Record, upload, or import a video',
  isComplete: (data) =>
    isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 0,
  getRoute: (getId) => {
    const projectId = getId('project');
    return isNonEmptyString(projectId) ? paths.project({ projectId }) : paths.allContent();
  },
  icon: 'download' as const,
  color: '#268713',
  linkType: LINK_TYPES.media,
};

export const FIRST_EDIT: OnboardingChecklistItemData = {
  name: 'Edit, trim, stitch, and more',
  isComplete: (data) => isNotNil(data.currentContact) && !!data.currentContact.hasEditSession,
  isEnabled: (data) =>
    isNotNil(data.currentContact?.medias.nodes) &&
    data.currentContact.medias.nodes.length > 0 &&
    !data.currentContact.hasEditSession,
  getMediaHashedId: (getId) => getId('media'),
  getRoute: (getId) => {
    const mediaHashedId = getId('editedMedia') ?? getId('media');
    return isNonEmptyString(mediaHashedId)
      ? paths.editMedia({ mediaHashedId: mediaHashedId.toString() })
      : undefined;
  },
  mediaFinderProps: {
    shouldUseMediaFinder: (data) =>
      isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 1,
    mediaFinderTitle: 'Select a media to edit',
    mediaFinderSubtitle: (
      <div>
        New to our editing tools? Try out our <a href={paths.anonymousEditor()}>interactive demo</a>
        .
      </div>
    ),
  },
  icon: 'edit' as const,
  color: '#DC9601',
  linkType: LINK_TYPES.edit,
  processingMessage: 'After processing, you can edit your video.',
};

export const FIRST_CUSTOMIZATION: OnboardingChecklistItemData = {
  name: 'Customize your video player',
  isComplete: (data) =>
    isNotNil(data.currentContact) && data.currentContact.hasCustomizedEmbedOptions,
  isEnabled: (data) =>
    isNotNil(data.currentContact?.medias.nodes) &&
    data.currentContact.medias.nodes.length > 0 &&
    !data.currentContact.hasCustomizedEmbedOptions,
  getMediaHashedId: (getId) => getId('editedMedia') ?? getId('media'),
  getRoute: (getId) => {
    const mediaHashedId = getId('editedMedia') ?? getId('media');
    return isNonEmptyString(mediaHashedId)
      ? `${paths.media({ mediaHashedId: mediaHashedId.toString() })}/customize`
      : undefined;
  },
  mediaFinderProps: {
    shouldUseMediaFinder: (data) =>
      isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 1,
    mediaFinderTitle: 'Select a media to customize',
    mediaFinderSubtitle: 'Adjust player color, set video thumbnail, add calls to action, and more.',
  },
  icon: 'paint' as const,
  color: '#D1451A',
  linkType: LINK_TYPES.customize,
  processingMessage: 'After processing, you can customize your video.',
};

export const FIRST_COMMENT: OnboardingChecklistItemData = {
  name: 'Comment on a video',
  isComplete: (data) => isNotNil(data.currentContact) && data.currentContact.hasAComment,
  isEnabled: (data) =>
    isNotNil(data.currentContact?.medias.nodes) &&
    data.currentContact.medias.nodes.length > 0 &&
    !data.currentContact.hasAComment,
  getMediaHashedId: (getId) => getId('editedMedia') ?? getId('media'),
  getRoute: (getId) => {
    const mediaHashedId = getId('editedMedia') ?? getId('media');
    return isNonEmptyString(mediaHashedId)
      ? `${paths.media({ mediaHashedId: mediaHashedId.toString() })}?active_tab=comments`
      : undefined;
  },
  mediaFinderProps: {
    shouldUseMediaFinder: (data) =>
      isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 1,
    mediaFinderTitle: 'Select a media to comment',
    mediaFinderSubtitle: 'Collaborate by leaving a comment or tagging other users.',
  },
  icon: 'audience' as const,
  color: '#DC9601',
  linkType: LINK_TYPES.comment,
  processingMessage: 'After processing, you can comment on your video.',
};

const FIRST_SHARE: OnboardingChecklistItemData = {
  name: 'Share and get a view',
  isComplete: (data) => isNotNil(data.currentContact) && !!data.currentContact.hasAPlay,
  isEnabled: (data) =>
    isNotNil(data.currentContact?.medias.nodes) &&
    data.currentContact.medias.nodes.length > 0 &&
    !data.currentContact.hasAPlay,
  getMediaHashedId: (getId) => getId('editedMedia') ?? getId('media'),
  getRoute: (getId) => {
    const mediaHashedId = getId('editedMedia') ?? getId('media');
    return isNonEmptyString(mediaHashedId)
      ? `${paths.media({ mediaHashedId: mediaHashedId.toString() })}?shareModalOpen=true`
      : undefined;
  },
  mediaFinderProps: {
    shouldUseMediaFinder: (data: DefaultChecklistInfoQuery) =>
      isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 1,
    mediaFinderTitle: 'Select a media to customize',
    mediaFinderSubtitle: 'Adjust player color, set video thumbnail, add calls to action, and more.',
  },
  icon: 'send' as const,
  color: '#AC12E0',
  linkType: LINK_TYPES.share,
  processingMessage: 'After processing, you can share your video.',
};

const FIRST_ANALYTICS: OnboardingChecklistItemData = {
  name: 'See the analytics',
  isComplete: (data) => isNotNil(data.currentContact) && !!data.currentContact.hasSeenAnalytics,
  isEnabled: (data) =>
    isNotNil(data.currentContact?.medias.nodes) && data.currentContact.medias.nodes.length > 0,
  getMediaHashedId: (getId) => getId('editedMedia') ?? getId('media'),
  getRoute: () => paths.analytics(),
  icon: 'stats' as const,
  color: '#2949E5',
  linkType: LINK_TYPES.analytics,
  processingMessage: 'After processing, you can view your analytics.',
};

export const checklist: OnboardingChecklist = {
  key: 'default',
  title: '',
  releaseDate: parse('09/24/2023', 'MM/dd/yyyy', new Date()),
  width: '272px',
  items: [
    FIRST_MEDIA,
    FIRST_EDIT,
    FIRST_CUSTOMIZATION,
    FIRST_SHARE,
    FIRST_COMMENT,
    FIRST_ANALYTICS,
  ],
};
